// Conditional bar chart for grouped engine results (reporting foundation R6).
// Renders ONLY when exactly one group-by field is set and the result carries at
// least one numeric column besides it: group values on X, the first numeric
// aggregation column on Y. Shared by the builder preview and the engine-mode
// saved viewer — both just pass the result + group_by and let this decide.
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import {
  formatCell,
  isNumericFieldType,
  type ReportEngineResult,
} from '@/services/reportEngineService';

/** Keep the chart readable — beyond this many groups a table works better. */
const MAX_BARS = 40;

export const EngineResultChart: React.FC<{
  result: ReportEngineResult;
  groupBy: string[];
}> = ({ result, groupBy }) => {
  const { t } = useTranslation('reports');

  if (groupBy.length !== 1 || result.rows.length === 0) return null;
  const groupKey = groupBy[0];
  const groupCol = result.columns.find((c) => c.key === groupKey) ?? result.columns[0];
  const valueCol = result.columns.find(
    (c) => c.key !== groupCol?.key && isNumericFieldType(c.type),
  );
  if (!groupCol || !valueCol) return null;

  const data = result.rows.slice(0, MAX_BARS).map((row) => ({
    name: formatCell(row[groupCol.key], groupCol.type),
    // Money can arrive as a formatted string — strip thousands separators.
    value: Number(String(row[valueCol.key] ?? 0).replace(/,/g, '')) || 0,
  }));

  return (
    <div className="mb-6">
      <p className="text-sm font-medium text-foreground mb-2">
        {t('chart.byGroup', '{{value}} by {{group}}', {
          value: valueCol.label,
          group: groupCol.label,
        })}
        {result.rows.length > MAX_BARS && (
          <span className="ml-2 text-xs text-muted-foreground font-normal">
            {t('chart.firstGroups', '— first {{count}} groups', { count: MAX_BARS })}
          </span>
        )}
      </p>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 12 }} width={72} />
          <Tooltip formatter={(v: number) => [formatCell(v, valueCol.type), valueCol.label]} />
          <Bar dataKey="value" fill="#f97316" radius={[4, 4, 0, 0]} maxBarSize={48} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default EngineResultChart;
